import React, { useEffect, useState } from 'react';
import { Box, Grid, Paper, Typography, Card, CardContent, CircularProgress, Button } from '@mui/material';
import { People, PersonAdd, CheckCircle, Cancel, MenuBook, Assignment, UploadFile, SupportAgent } from '@mui/icons-material';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import { useSchool } from '../contexts/SchoolContext';
import Spinner from '../components/Spinner';

const SchoolDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { school, staff, students, loading } = useSchool();
  const [subjects, setSubjects] = useState<any[]>([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);

  useEffect(() => {
    if (school?._id) {
      setLoadingSubjects(true);
      api.get(`/schools/${school._id}/subjects`, {
        headers: { 'x-auth-token': localStorage.getItem('school_token') }
      })
        .then(res => setSubjects(res.data))
        .catch(err => console.error('Failed to load subjects', err))
        .finally(() => setLoadingSubjects(false));
    }
  }, [school]);

  if (loading) {
    return <Box display="flex" justifyContent="center" mt={4}><Spinner /></Box>;
  }

  const stats = [
    {
      title: 'Total Students',
      value: students.length,
      icon: <People sx={{ fontSize: 40, color: 'primary.main' }} />,
    },
    {
      title: 'Total Staff',
      value: staff.length,
      icon: <PersonAdd sx={{ fontSize: 40, color: 'secondary.main' }} />,
    },
    {
      title: 'Taught Subjects',
      value: subjects.length,
      icon: <MenuBook sx={{ fontSize: 40, color: 'info.main' }} />,
    },
    {
      title: 'Subscription Status',
      value: school?.subscriptionActive ? 'Active' : 'Inactive',
      icon: school?.subscriptionActive ?
        <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} /> :
        <Cancel sx={{ fontSize: 40, color: 'error.main' }} />,
    },
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
        Dashboard Overview
      </Typography>

      <Grid container spacing={3}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Box sx={{ mr: 2 }}>
                  {stat.icon}
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.title}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 180 }}>
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Assignment sx={{ color: 'primary.main' }} /> Term Results
              </Typography>
              <Typography color="text.secondary">
                Review teacher-submitted results and performance for the current active term.
              </Typography>
            </Box>
            <Button variant="contained" sx={{ mt: 3, alignSelf: 'flex-start' }} onClick={() => navigate('/school/term-results')}>
              View Term Results
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 180 }}>
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <MenuBook sx={{ color: 'primary.main' }} /> Report Cards
              </Typography>
              <Typography color="text.secondary">
                Preview and print student report cards for every class in the school.
              </Typography>
            </Box>
            <Button variant="contained" sx={{ mt: 3, alignSelf: 'flex-start' }} onClick={() => navigate('/school/report-cards')}>
              View Report Cards
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 180 }}>
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <SupportAgent sx={{ color: 'primary.main' }} /> Parent Tickets
              </Typography>
              <Typography color="text.secondary">
                View parent support tickets submitted by parents and respond to or close them.
              </Typography>
            </Box>
            <Button variant="contained" sx={{ mt: 3, alignSelf: 'flex-start' }} onClick={() => navigate('/school/parent-tickets')}>
              View Parent Tickets
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 180 }}>
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Assignment sx={{ color: 'primary.main' }} /> Term History
              </Typography>
              <Typography color="text.secondary">
                View term results history and previous submissions for all completed or active terms.
              </Typography>
            </Box>
            <Button variant="contained" sx={{ mt: 3, alignSelf: 'flex-start' }} onClick={() => navigate('/school/term-history')}>
              View Term History
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 180 }}>
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <UploadFile sx={{ color: 'primary.main' }} /> Exam Ticket Settings
              </Typography>
              <Typography color="text.secondary">
                Configure the exam/hall ticket subjects, instructions, and signature displayed to candidates.
              </Typography>
            </Box>
            <Button variant="contained" sx={{ mt: 3, alignSelf: 'flex-start' }} onClick={() => navigate('/school/exam-ticket-settings')}>
              Open Exam Ticket Settings
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Recent Students
            </Typography>
            {students.slice(0, 5).map((student) => (
              <Box key={student._id} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #eee' }}>
                <Typography>{student.name}</Typography>
                <Typography variant="body2" color="text.secondary">{student.email}</Typography>
              </Box>
            ))}
            {students.length === 0 && (
              <Typography color="text.secondary">No students yet</Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Staff Overview
            </Typography>
            {staff.map((member) => (
              <Box key={member._id} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #eee' }}>
                <Typography>{member.name}</Typography>
                <Typography variant="body2" color="text.secondary">{member.role}</Typography>
              </Box>
            ))}
            {staff.length === 0 && (
              <Typography color="text.secondary">No staff members yet</Typography>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Subjects Offered (Configured by Teachers)
            </Typography>
            {loadingSubjects ? (
              <CircularProgress size={24} />
            ) : subjects.length > 0 ? (
              <Grid container spacing={2}>
                {subjects.map((sub, idx) => (
                  <Grid item xs={12} sm={6} md={4} key={idx}>
                    <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                      <Typography variant="subtitle1" fontWeight={500}>{sub.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Taught by: {sub.staff && sub.staff.length > 0 
                          ? sub.staff.map((s: any) => s.name || s).join(', ') 
                          : 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography color="text.secondary">No subjects added by staff yet.</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SchoolDashboard;